import React from "react";
import { Phone, Mail, MapPin, ChefHat, UtensilsCrossed, Wallet, CheckCircle2, Info } from "lucide-react";

export default function CustomerProviderCard({
  provider,
  customer,
  onVerifyClick
}) {
  // Helper to extract initials
  const getInitials = (name) => {
    return name
      ? name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
      : "";
  };



  const providerInitials = getInitials(provider.name) || "TT";
  const customerInitials = getInitials(customer.name) || "CS";

  // Balance calculations
  const balanceAmount = Math.abs(customer.current_balance || 0);
  const isDue = (customer.current_balance || 0) < 0 || customer.balance_type === "due";

  return (
    <div className="bg-white border border-border-primary/80 rounded-2xl p-4 sm:p-5 flex flex-col lg:flex-row gap-6 mb-6 shadow-2xs w-full overflow-visible relative">

      {/* LEFT SIDE: Provider Details */}
      <div className="flex-[1.4] w-full flex flex-col sm:flex-row justify-between items-start gap-4">
        {/* Sub-Left: Logo & Details */}
        <div className="flex gap-4 items-start text-left">
          {provider.logoUrl ? (
            <img
              src={provider.logoUrl}
              alt={provider.name}
              className="w-14 h-14 rounded-full object-cover border border-border-primary shrink-0 shadow-2xs"
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-[#1F1F1F] text-white font-extrabold text-lg flex items-center justify-center shrink-0 shadow-2xs">
              {providerInitials}
            </div>
          )}

          <div className="space-y-1.5 text-left">
            <div className="flex flex-wrap items-center gap-2">
              {/* Provider Service Name */}
              <h2 className="text-base font-extrabold text-text-primary font-display leading-tight capitalize">
                {provider.name}
              </h2>

              {/* Food Type Badge (Veg/Non-veg) */}
              {provider.foodType && (
                <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${provider.foodType.toLowerCase() === "veg"
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-rose-50 text-rose-700 border border-rose-200"
                  }`}>
                  {provider.foodType}
                </span>
              )}

              {/* Delivery Type (Pickup/Delivery) with Info Icon & Tooltip */}
              {provider.deliveryType && (
                <div className="relative group/deliveryInfo inline-flex items-center gap-1">
                  <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200">
                    {provider.deliveryType}
                  </span>
                  <div className="inline-flex items-center justify-center cursor-pointer p-0.5 text-text-muted hover:text-text-primary transition-colors">
                    <Info className="w-3.5 h-3.5 text-blue-500" />
                  </div>

                  {/* Info Tooltip */}
                  <div className="absolute left-0 top-full mt-2 hidden group-hover/deliveryInfo:block w-56 bg-gray-900 text-white text-[10px] p-2.5 rounded-lg shadow-xl z-[100] text-left font-medium leading-normal pointer-events-none transition-all animate-fade-in">
                    Indicates whether the kitchen provider offers for home delivery services or self-pickup options for your tiffin meals or both.
                    <div className="absolute bottom-full left-4 border-4 border-transparent border-b-gray-900"></div>
                  </div>
                </div>
              )}
            </div>

            {/* Owner/Chef Details */}
            {provider.ownerName && (
              <div className="flex items-center gap-1.5 text-xs text-text-secondary font-medium">
                <ChefHat className="w-3.5 h-3.5 text-text-muted shrink-0" />
                <span><strong className="text-text-primary">{provider.ownerName}</strong></span>
              </div>
            )}

            {/* Cuisines */}
            {provider.cuisines && provider.cuisines.length > 0 && (
              <div className="flex items-center gap-1.5 text-[11px] text-text-secondary">
                <UtensilsCrossed className="w-3.5 h-3.5 text-text-muted shrink-0" />
                <span className="font-semibold text-text-primary/95">
                  {provider.cuisines.map(c => c.name).join(" · ")}
                </span>
              </div>
            )}

            {/* Provider Address */}
            {provider.address && (
              <div className="flex items-start gap-1 text-[11px] text-text-muted leading-tight mt-1 max-w-xs sm:max-w-md">
                <MapPin className="w-3.5 h-3.5 text-text-muted shrink-0 mt-0.5" />
                <span className="text-gray-500 text-[11px]">{provider.address}</span>
              </div>
            )}
          </div>
        </div>

        {/* Sub-Right: View Meal Plan & Contact Actions */}
        <div className="flex flex-col items-start sm:items-end justify-start gap-2 self-start w-full sm:w-auto mt-3 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-border-primary/40 shrink-0">
          {provider.qrLink && (
            <a
              href={provider.qrLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] font-extrabold text-brand-red hover:text-brand-red-hover hover:underline transition-all sm:mb-1"
            >
              View More Details
            </a>
          )}

          <div className="flex items-center gap-2">
            {provider.phone && (
              <a
                href={`tel:${provider.phone}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-bg-secondary hover:bg-brand-red-light/30 text-text-secondary hover:text-brand-red rounded-lg text-[10px] font-bold transition-all border border-border-primary cursor-pointer shrink-0"
                title="Call Kitchen"
              >
                <Phone className="w-3 h-3" />
                <span>Call Kitchen</span>
              </a>
            )}
            {provider.email && (
              <a
                href={`mailto:${provider.email}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-bg-secondary hover:bg-brand-red-light/30 text-text-secondary hover:text-brand-red rounded-lg text-[10px] font-bold transition-all border border-border-primary cursor-pointer shrink-0"
                title="Email Kitchen"
              >
                <Mail className="w-3 h-3" />
                <span>Email</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Vertical divider on large screens, horizontal on small */}
      <div className="hidden lg:block w-[1px] bg-border-primary/80 self-stretch my-1" />
      <div className="block lg:hidden h-[1px] bg-border-primary/80 w-full" />

      {/* RIGHT SIDE: Customer Details & Balance */}
      <div className="flex-1 w-full flex flex-col sm:flex-row gap-4 items-start justify-between">
        <div className="flex gap-3 text-left w-full sm:w-auto">
          {/* Customer Avatar / Initials */}
          <div className="w-14 h-14 text-[18px] rounded-full bg-orange-50 text-orange-600 font-bold text-sm flex items-center justify-center shrink-0 border border-orange-200 shadow-2xs">
            {customerInitials}
          </div>

          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-extrabold text-text-primary leading-tight font-display">
                Welcome, {customer.name || "Guest"}
              </span>

              {customer.isVerified ? (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider bg-success-bg text-success border border-success/20">
                  <CheckCircle2 className="w-2.5 h-2.5" />
                  <span>Verified</span>
                </span>
              ) : (
                <button
                  onClick={onVerifyClick}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider bg-brand-red-light text-brand-red border border-brand-red/20 hover:bg-brand-red hover:text-white transition-all cursor-pointer"
                >
                  Verify now
                </button>
              )}
            </div>

            {/* Email and Phone */}
            <div className="flex flex-col gap-0.5 text-[10px] text-text-secondary leading-normal">
              {customer.email && (
                <span className="flex items-center gap-1.5">
                  <Mail className="w-3 h-3 text-text-muted shrink-0" />
                  <span>{customer.email}</span>
                </span>
              )}
              {customer.phone && (
                <span className="flex items-center gap-1.5">
                  <Phone className="w-3 h-3 text-text-muted shrink-0" />
                  <span>{customer.phone}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Ledger Dues / Advance Balance status */}
        <div className="w-full sm:w-auto p-3.5 rounded-xl border border-border-primary/80 bg-bg-secondary/40 text-left sm:text-right flex items-center sm:flex-col gap-3 sm:gap-1 shrink-0 justify-between">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-text-muted">
            <Wallet className="w-3.5 h-3.5" />
            <span>{isDue ? "Dues Outstanding" : "Advance Balance"}</span>
          </div>
          <div className={`text-lg font-black font-mono tracking-tight ${isDue ? "text-brand-red" : "text-emerald-600"
            }`}>
            {isDue ? "-" : ""}
            {provider.currencySymbol}
            {balanceAmount.toFixed(2)}
          </div>
        </div>
      </div>

    </div>
  );
}
